import { lazy, Suspense, useState, useEffect, useRef, useCallback } from "react";
import type { TouchEvent } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "../integrations/supabase/client";
import { Sidebar } from "../components/navigation/Sidebar";
import { MobileHeader } from "../components/navigation/MobileHeader";
import { MobileBottomNav } from "../components/navigation/MobileBottomNav";
import { AlertWarning } from "../components/shared/ui/AlertWarning";
import { useUserProfileData } from "../features/profile/hooks/useUserProfileData";
import { useStations } from "../features/dashboard/hooks/useDashboardData";
import { useMemberships } from "../features/memberships/hooks/useMemberships";
import { hasStationAccess, usePermissions } from "../utils/permissions";
import { RealtimeService } from "../features/analysis/services/realtime.service";
import { useSwipeGesture } from "../hooks/useSwipeGesture";
import { useIsMobile } from "../hooks/useMediaQuery";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { SystemRole } from "../types/common";
import { logErrorToConsole } from "../utils/errorHandlerUtils";
import { withTimeout } from "@/lib/withTimeout";
import { queryKeys } from "../lib/queryKeys";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { CloudOff } from "lucide-react";
import { PageSkeleton } from "../components/shared/ui/PageSkeleton";
import { Skeleton } from "../components/ui/skeleton";
import {
  disableWebPush,
  syncWebPushSubscription,
} from "../features/pwa/services/webPush.service";

/**
 * Plazo máximo para la limpieza de avisos al cerrar sesión. Es un paso de
 * cortesía: si tarda más que esto, se abandona y se cierra sesión igualmente.
 */
const SIGN_OUT_CLEANUP_TIMEOUT_MS = 3000;

// El panel trae Select, filtros y tarjetas de alerta (~60 kB con Radix Select y
// floating-ui) y solo se abre con la campana: fuera del chunk de entrada. Se
// precarga al pasar el puntero o enfocar la campana (ver `prefetchNotifications`).
const loadNotificationsPanel = () =>
  import("../features/notifications/components/NotificationsPanel");
const NotificationsPanel = lazy(() =>
  loadNotificationsPanel().then((module) => ({ default: module.NotificationsPanel }))
);
const prefetchNotifications = () => {
  void loadNotificationsPanel().catch(() => undefined);
};

function NotificationsPanelSkeleton() {
  return (
    <div role="status" aria-label="Cargando notificaciones" className="space-y-4 p-4">
      <div className="flex items-center justify-between" aria-hidden="true">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-9 w-9 rounded-lg" />
      </div>
      <Skeleton className="h-10 w-full" aria-hidden="true" />
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="space-y-2 rounded-lg border border-border p-3" aria-hidden="true">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      ))}
    </div>
  );
}

export function DashboardLayout() {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const isOnline = useOnlineStatus();

  // El drawer móvil y el sidebar de escritorio son estados distintos. Un único
  // booleano hacía que redimensionar/rotar el dispositivo pudiera abrir el menú
  // móvil o expandir el desktop de forma inesperada.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [desktopSidebarExpanded, setDesktopSidebarExpanded] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [inactiveWarningDismissed, setInactiveWarningDismissed] = useState(false);
  const [isRadixScrollLocked, setIsRadixScrollLocked] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const notificationsRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  // <main> es el ÚNICO contenedor que desplaza, y no se reinicia solo al
  // navegar: sin esto se entra a la página nueva a media altura, justo el
  // desplazamiento que había dejado la anterior.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 });
  }, [pathname]);

  // El panel de notificaciones es modal: el foco no debe escaparse al contenido
  // de detrás, que en móvil queda completamente tapado.
  useFocusTrap(notificationsRef, showNotifications);

  useEffect(() => {
    if (!isMobile) setMobileSidebarOpen(false);
  }, [isMobile]);

  // Escape cierra cualquier superficie temporal.
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMobileSidebarOpen(false);
      setShowNotifications(false);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  // Radix bloquea el scroll de <body> al abrir un Dialog, pero aquí el único
  // contenedor que desplaza es <main>: sin escuchar esa marca, el fondo seguía
  // moviéndose detrás del modal.
  useEffect(() => {
    const body = document.body;
    const readLock = () =>
      body.hasAttribute("data-scroll-locked") || body.style.overflow === "hidden";

    setIsRadixScrollLocked(readLock());
    const observer = new MutationObserver(() => setIsRadixScrollLocked(readLock()));
    observer.observe(body, {
      attributes: true,
      attributeFilter: ["data-scroll-locked", "style"],
    });
    return () => observer.disconnect();
  }, []);

  // El contenido principal es el único contenedor de scroll de la aplicación.
  // Esto evita depender del scroll implícito de html/body (que Chrome puede dejar
  // sin rueda cuando ambos recortan el overflow horizontal) y mantiene el sidebar
  // y la cabecera estables. Los overlays bloquean este contenedor explícitamente.
  const isContentScrollLocked =
    mobileSidebarOpen || showNotifications || isRadixScrollLocked;

  const { userProfile, isUserActive } = useUserProfileData();
  const permissions = usePermissions(userProfile);
  const stationsQuery = useStations(userProfile);
  const { memberships, isError: membershipError } = useMemberships();
  const previousAccessScopeRef = useRef<string | null>(null);

  // Another administrator can grant or revoke memberships from another
  // session. When that scope changes, reset dependent data so every active
  // query runs again under the current RLS permissions and stale offline data
  // from revoked stations is discarded.
  useEffect(() => {
    if (!userProfile) return;

    const accessScope = JSON.stringify({
      isActive: userProfile.is_active,
      role: userProfile.role,
      stations: [...(userProfile.station_roles ?? [])]
        .map(({ station_id, station_role, is_active }) => ({
          stationId: station_id,
          stationRole: station_role,
          isActive: is_active,
        }))
        .sort((a, b) => a.stationId.localeCompare(b.stationId)),
    });

    const previousAccessScope = previousAccessScopeRef.current;
    previousAccessScopeRef.current = accessScope;

    if (previousAccessScope !== null && previousAccessScope !== accessScope) {
      void queryClient.resetQueries({
        predicate: (query) => query.queryKey[0] !== "userProfile",
      });
    }
  }, [queryClient, userProfile]);

  // hasStationAccess ya deja pasar al superadmin.
  const userStations = (stationsQuery.data ?? []).filter((station) =>
    hasStationAccess(userProfile, station.id)
  );

  // Centralized real-time subscription for alerts
  useEffect(() => {
    const channel = RealtimeService.createTableSubscription(
      'alerts',
      () => {
        // Las alertas se cachean bajo useTableData → ['tableData', 'alerts', …];
        // invalidar solo queryKeys.alerts() dejaba el panel sin refrescar en vivo.
        queryClient.invalidateQueries({ queryKey: queryKeys.alerts() });
        queryClient.invalidateQueries({ queryKey: ['tableData', 'alerts'] });
      }
    );

    RealtimeService.subscribeChannel(channel);

    return () => {
      RealtimeService.unsubscribeChannel(channel);
    };
  }, [queryClient]);

  // Renueva la asociación endpoint↔usuario al entrar, sin volver a pedir
  // permiso. El permiso sólo se solicita desde el botón explícito del perfil.
  useEffect(() => {
    void syncWebPushSubscription().catch(() => false);
  }, []);

  const handleSignOut = async () => {
    try {
      // En un dispositivo compartido no deben seguir llegando alertas de la
      // cuenta anterior después de cerrar sesión. Pero eso es CORTESÍA: cerrar
      // sesión es lo que el usuario pidió y no puede quedar rehén de ello.
      //
      // El plazo no es decorativo. Las APIs de service worker pueden dejar una
      // promesa pendiente para siempre en vez de rechazarla (ver
      // `activeSubscription`), y un `await` sin tope convierte ese cuelgue en
      // un botón que no hace nada, sin error ni rastro en la consola.
      await withTimeout(
        disableWebPush(),
        SIGN_OUT_CLEANUP_TIMEOUT_MS,
      ).catch((error) => {
        logErrorToConsole(error, {
          module: 'DashboardLayout',
          operation: 'disableWebPushOnSignOut',
        });
      });
      // App.tsx escucha SIGNED_OUT: limpia la caché de queries y el árbol de
      // rutas cae solo a /login al quedar la sesión en null.
      await supabase.auth.signOut();
    } catch (error) {
      logErrorToConsole(error, { module: 'DashboardLayout', operation: 'signOut' });
      // Si hasta el propio signOut falla (red caída, servidor de auth
      // inaccesible), la sesión local debe irse igual: quedarse dentro tras
      // pedir salir es lo peor que puede pasar en un dispositivo compartido.
      await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
    }
  };

  const hasActiveStations = userStations.some(station => station.is_active);

  // Superadmin, o con membresías activas. Si la consulta de membresías falla, se
  // recurre a las estaciones visibles para no dejar el menú vacío.
  const hasActiveMemberships = userProfile?.role === SystemRole.SUPERADMIN ||
    (!membershipError && memberships.length > 0) ||
    (membershipError && userStations.length > 0);

  // Gestos de swipe para móviles
  const swipeGestures = useSwipeGesture({
    onSwipeRight: () => {
      if (isMobile && !mobileSidebarOpen) {
        setMobileSidebarOpen(true);
      }
    },
    onSwipeLeft: () => {
      if (isMobile && mobileSidebarOpen) {
        setMobileSidebarOpen(false);
      }
    },
    minSwipeDistance: 80,
    maxSwipeTime: 300,
  });

  // Un arrastre que nace dentro de una tabla ancha o de una fila de chips es
  // scroll de ESE contenedor, no un gesto del menú.
  const swipeBlockedRef = useRef(false);
  const startsInHorizontalScroller = useCallback((target: EventTarget | null) => {
    let node = target instanceof Element ? target : null;
    while (node && node !== mainRef.current) {
      if (node.classList.contains("scroll-x")) return true;
      const { overflowX } = window.getComputedStyle(node);
      if (
        (overflowX === "auto" || overflowX === "scroll") &&
        node.scrollWidth > node.clientWidth
      ) {
        return true;
      }
      node = node.parentElement;
    }
    return false;
  }, []);

  // Abrir el menú solo se gesta en el borde izquierdo, como en iOS/Android: un
  // arrastre a la derecha en mitad de la pantalla es del contenido, no del
  // shell. Para CERRARLO vale cualquier punto (el drawer ya ocupa el frente).
  const EDGE_ZONE_PX = 28;

  const handleTouchStart = (event: TouchEvent) => {
    const startX = event.targetTouches[0]?.clientX ?? 0;
    const outsideEdge = !mobileSidebarOpen && startX > EDGE_ZONE_PX;
    swipeBlockedRef.current =
      outsideEdge || startsInHorizontalScroller(event.target);
    // El hook se avisa siempre para que reinicie su estado interno; al no
    // reenviarle el movimiento, un gesto bloqueado nunca llega a dispararse.
    swipeGestures.onTouchStart(event);
  };
  const handleTouchMove = (event: TouchEvent) => {
    if (!swipeBlockedRef.current) swipeGestures.onTouchMove(event);
  };
  const handleTouchEnd = () => {
    if (!swipeBlockedRef.current) swipeGestures.onTouchEnd();
  };

  return (
    <div className="flex h-[100dvh] w-full flex-col overflow-hidden bg-background md:flex-row">
      <a
        href="#main-content"
        className="fixed left-3 top-3 z-skip flex min-h-11 -translate-y-20 items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-lg transition-transform focus:translate-y-0"
      >
        Saltar al contenido
      </a>

      <MobileHeader
        onMenuToggle={() => setMobileSidebarOpen(true)}
        isMenuOpen={mobileSidebarOpen}
        // La campana abre el MISMO panel que en escritorio. Antes navegaba a
        // /alerts, que ya está a un toque en la barra inferior: en móvil el
        // panel de notificaciones no tenía forma de abrirse.
        onNotificationsToggle={() => setShowNotifications((open) => !open)}
        onNotificationsIntent={prefetchNotifications}
        showNotifications={showNotifications}
      />

      {mobileSidebarOpen && (
        // Fuera del orden de tabulación: al estar ANTES en el DOM que el panel
        // que cierra, el Tab llegaba a "Cerrar menú" antes que al contenido.
        // Escape sigue cerrando; el clic también.
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          className="fixed inset-0 z-overlay bg-black/55 backdrop-blur-[1px] animate-fade-in md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          // El velo tapa a <main>, así que sin estos manejadores el arrastre
          // hacia la izquierda para CERRAR el menú no llegaba a ninguna parte.
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        />
      )}

      <Sidebar
        isExpanded={desktopSidebarExpanded}
        isMobileOpen={mobileSidebarOpen}
        onExpandedChange={setDesktopSidebarExpanded}
        onMobileOpenChange={setMobileSidebarOpen}
        hasActiveStations={hasActiveStations}
        hasActiveMemberships={hasActiveMemberships}
        isUserActive={isUserActive}
        permissions={permissions}
        onSignOut={handleSignOut}
        onNotificationsToggle={() => setShowNotifications((open) => !open)}
        onNotificationsIntent={prefetchNotifications}
        showNotifications={showNotifications}
      />

      <main
        ref={mainRef}
        id="main-content"
        tabIndex={-1}
        // El hueco inferior lo publica la propia barra móvil en --bottom-nav-h;
        // sin overflow-x-hidden, para que un desbordamiento se VEA en desarrollo
        // en vez de quedar recortado (html/body ya son la red de seguridad).
        className={`gutter-safe min-h-0 min-w-0 flex-1 overscroll-y-contain py-4 pb-[calc(var(--bottom-nav-h,4rem)_+_1rem)] sm:py-6 md:pb-[calc(env(safe-area-inset-bottom,0px)_+_1.5rem)] ${
          isContentScrollLocked ? "overflow-y-hidden" : "overflow-y-auto"
        } ${
          desktopSidebarExpanded ? "md:ml-64" : "md:ml-20"
        } transition-[margin] duration-moderate ease-smooth focus:outline-none`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="mx-auto w-full max-w-[1680px]">
          {!isOnline && (
            <div className="mb-4 flex items-center gap-2 border-l-2 border-warning bg-transparent py-2 pl-3 pr-1 text-sm text-foreground" role="status">
              <CloudOff className="h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
              Sin conexión: mostrando los últimos datos guardados. Para hacer cambios, vuelve a conectarte.
            </div>
          )}
          <AlertWarning
            show={!isUserActive && !inactiveWarningDismissed}
            onDismiss={() => setInactiveWarningDismissed(true)}
            title="Cuenta Desactivada"
            description="Tu cuenta está desactivada. Contacta al administrador para obtener acceso."
          />

          {/* Suspense PROPIO del contenido: al entrar por enlace directo a una
              ruta cuyo chunk aún no llegó, el shell (menú y cabecera) sigue en
              pie y solo el hueco de la página muestra el esqueleto. La clave
              por ruta reproduce una entrada corta al navegar, no en cada
              refetch: los datos que se refrescan no vuelven a animarse. */}
          <div key={pathname} className="animate-page-in">
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </div>
        </div>
      </main>

      <MobileBottomNav />

      {showNotifications && (
        <>
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            className="fixed inset-0 z-overlay bg-black/35 backdrop-blur-[1px] animate-fade-in"
            onClick={() => setShowNotifications(false)}
          />
          <aside
            ref={notificationsRef}
            role="dialog"
            aria-modal="true"
            aria-label="Notificaciones"
            // Recibe el foco mientras el chunk del panel aún carga (ver useFocusTrap).
            tabIndex={-1}
            // Entra deslizando desde la derecha (en móvil ocupa toda la
            // pantalla): aparecer de golpe no dice de dónde ha salido ni cómo
            // se vuelve. `overscroll-contain` impide que al llegar al final
            // arrastre la página de detrás.
            className="fixed inset-y-0 right-0 z-drawer w-full max-w-sm animate-in slide-in-from-right-full duration-moderate overflow-y-auto overscroll-contain focus:outline-none border-l border-border bg-card pb-safe pr-safe pt-safe shadow-xl"
          >
            <Suspense fallback={<NotificationsPanelSkeleton />}>
              <NotificationsPanel onClose={() => setShowNotifications(false)} />
            </Suspense>
          </aside>
        </>
      )}
    </div>
  );
}
